import { MIN_TOKEN_LENGTH_PG_TRGM, shortTokensDropped, tokenize } from '@medcom/shared-ui/utils/tokenSearch.ts';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchLabResults } from '../../../api/lab-result.queries.ts';
import type { LabResultsSearchResult } from '../../../generated/openapi/befunde-api';
import type { UpdateEvent } from '../../../socket/types.ts';
import { useSubscriptions } from '../../../socket/useSubscription.ts';
import { useUpdatedBefundStore } from '../../../store/useUpdatedBefundStore.ts';

const DEFAULT_PAGE_SIZE = 100;

export type Pageable = {
  page: number;
  pageSize: number;
};

export type PagedBefundeListState = {
  search: string;
  searchTokens: string[];
  pageable: Pageable;
  loading: boolean;
  error: boolean;
  loadedItems: LabResultsSearchResult[];
  hasMore: boolean;
  timestamp: Date;
};

const DEFAULT_PAGEABLE: Pageable = {
  page: 1,
  pageSize: DEFAULT_PAGE_SIZE,
};

export const useBefundListState = (myLabIdentifiers: string[] | undefined) => {
  const [state, setState] = useState<PagedBefundeListState>({
    search: '',
    searchTokens: [],
    loading: true,
    error: false,
    pageable: DEFAULT_PAGEABLE,
    loadedItems: [],
    hasMore: false,
    timestamp: new Date(),
  });

  const validBackendTokens = tokenize(state.search, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM });
  const searchHasOnlyShortTokens = state.search.trim() !== '' && validBackendTokens.length === 0;
  const droppedTokens = shortTokensDropped(state.search);

  const {
    data: labResults,
    isLoading: labResultsLoading,
    error: labResultsError,
  } = useSearchLabResults(
    {
      page: state.pageable.page,
      pageSize: state.pageable.pageSize,
      // Send only tokens that pass the backend's pg_trgm minimum length.
      search: validBackendTokens.join(' '),
    },
    state.timestamp,
    !searchHasOnlyShortTokens
  );

  useEffect(() => {
    if (!labResultsLoading) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(prev => {
        const updated = new Map(prev.loadedItems.map(e => [e.id, e]));
        (labResults?.items || []).forEach((labResult: LabResultsSearchResult) => {
          updated.set(labResult.id, labResult);
        });
        return {
          ...prev,
          loading: false,
          errorAtPage: !!labResultsError,
          loadedItems: Array.of(...updated.values()),
          hasMore: labResults?.items?.length === DEFAULT_PAGE_SIZE,
        };
      });
    }
  }, [labResults, labResultsLoading, labResultsError]);
  const [newBefundeAvailable, setNewBefundeAvailable] = useState(false);
  const [outdatedBefundIds, setOutdatedBefundIds] = useState<string[]>([]);
  const updatedBefund = useUpdatedBefundStore(state => state.befund);

  const callback = useCallback(
    (e: UpdateEvent) => {
      if (e.type === 'befund-added') {
        setNewBefundeAvailable(true);
      }
      if (e.type === 'befund-updated' && labResults && labResults.items.some(lr => lr.id === e.id)) {
        // biome-ignore lint/style/noNonNullAssertion: pre-checked
        setOutdatedBefundIds((prev: string[]) => (prev.includes(e.id!) ? prev : [...prev, e.id!]));
      }
    },
    [labResults]
  );
  const subscriptionOptions = useMemo(
    () =>
      (myLabIdentifiers || []).flatMap(li => [
        { type: 'befund-added', tenantId: li === '*:*' ? undefined : li },
        { type: 'befund-updated', tenantId: li === '*:*' ? undefined : li },
      ]),
    [myLabIdentifiers]
  );
  useSubscriptions(subscriptionOptions, callback);

  useEffect(() => {
    if (updatedBefund) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setState(prev => ({
        ...prev,
        loadedItems: prev.loadedItems.map(result =>
          result.id === updatedBefund.id
            ? {
                ...result,
                timestamp: updatedBefund.befund.timestamp,
                countPathological: updatedBefund.befund.countPathological,
                labResultStatus: updatedBefund.befund.observationRequest.labResultStatus,
                unread: false,
              }
            : result
        ),
      }));
      setOutdatedBefundIds((prev: string[]) => (!prev.includes(updatedBefund.id) ? prev : prev.filter(id => id !== updatedBefund.id)));
    }
  }, [updatedBefund]);

  const nextPage = useCallback(() => {
    setState(prev => {
      return {
        ...prev,
        loading: true,
        error: false,
        pageable: { ...prev.pageable, page: prev.pageable.page + 1 },
        hasMore: false,
      };
    });
  }, []);

  const search = useCallback((value: string) => {
    setNewBefundeAvailable(false);
    setOutdatedBefundIds([]);
    setState({
      search: value,
      // Use the same pg_trgm minimum length as the backend so Markable only highlights
      // tokens that the backend actually searched for.
      searchTokens: tokenize(value, { minTokenLength: MIN_TOKEN_LENGTH_PG_TRGM }),
      pageable: DEFAULT_PAGEABLE,
      loading: true,
      error: false,
      loadedItems: [],
      hasMore: false,
      timestamp: new Date(),
    });
  }, []);

  const reload = useCallback(() => {
    setNewBefundeAvailable(false);
    setOutdatedBefundIds([]);
    setState(prev => ({
      ...prev,
      pageable: DEFAULT_PAGEABLE,
      loading: true,
      error: false,
      loadedItems: [],
      hasMore: false,
      timestamp: new Date(),
    }));
  }, []);

  return {
    state,
    nextPage,
    search,
    outdatedBefundIds,
    newBefundeAvailable,
    reload,
    droppedTokens,
    searchHasOnlyShortTokens,
  };
};
